import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { getDisplayDateFormat } from "helpers/DatesHelper";
import {
    DEFAULT_PAGE_SIZE_OPTION,
    handlePageClick,
    handlePageSizeChange,
} from "helpers/PaginationHelper";
import {
    CustomTable,
    HeaderFilter,
    Input,
    OptionType,
    Pagination,
    TableColumn,
    TableType,
    Tooltip,
    useDebounced,
    useGetAdUserAccounts,
    useTitle,
} from "modules/common";
import React, { useEffect, useMemo, useState } from "react";
import { SortingRule } from "react-table";
import { AdAccounts } from "services";

const PAGE_NEIGHBOURS = 1;
const INPUT_DEBOUNCE_TIME = 500; //miliseconds

export function formatLongText(input: string, maxLength: number = 30): JSX.Element {
    if (!input || input.length <= maxLength) {
        return <div>{input}</div>;
    }

    const truncated = input.substring(0, maxLength) + "...";
    const tooltip = (
        <Tooltip tooltipText={input} tooltipPosition="top" id="full-screen-tooltip">
            {truncated}
        </Tooltip>
    );

    return tooltip;
}

export const ActiveDomainUsers: React.FC = () => {
    useTitle("Administracija korisnika - eSavjetnik");

    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);
    const [currentPage, setCurrentPage] = useState(1);
    const [sortBy, setSortBy] = useState<SortingRule<object>>();
    const [search, setSearch] = useState("");
    const debouncedSearch = useDebounced(search, INPUT_DEBOUNCE_TIME);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const { data: adUsers, isFetching: isLoading } = useGetAdUserAccounts(
        {
            pageNumber: currentPage,
            pageSize: parseInt(pageSize.value),
            search: debouncedSearch,
            sortField: sortBy?.id,
            descending: sortBy?.desc,
        },
        true
    );

    const tableColumns = useMemo(() => {
        const columns: TableColumn<AdAccounts>[] = [
            {
                Header: "IME KORISNIKA",
                accessor: "fullName",
            },
            {
                Header: "KORISNIČKO IME",
                accessor: "username",
            },
            {
                Header: "OPIS",
                accessor: "description",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return formatLongText(original.description);
                },
            },
            /*
            {
                Header: "IME ZA PRIKAZ",
                accessor: "displayName",
            },
            {
                Header: "IME",
                accessor: "name",
            },
            */
            {
                Header: "TITULA",
                accessor: "title",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return formatLongText(original.title);
                },
            },
            {
                Header: "ODJEL",
                accessor: "department",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return formatLongText(original.department);
                },
            },
            {
                Header: "EMAIL",
                accessor: "email",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return (
                        <a href={`mailto:${original.email}`} className="email-link-cell">
                            {original.email}
                        </a>
                    );
                },
            },
            /*
            {
                Header: "TELEFON",
                accessor: "phoneNumber",
            },
            {
                Header: "MOBILNI TELEFON",
                accessor: "mobile",
            },
            {
                Header: "KUĆNI TELEFON",
                accessor: "homePhone",
            },
            */
            {
                Header: "DATUM PROMJENE STATUSA",
                accessor: "whenChanged",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return <span>{getDisplayDateFormat(original.whenChanged, true)}</span>;
                },
            },
            {
                Header: "DATUM KREIRANJA RAČUNA",
                accessor: "whenCreated",
                Cell: ({ row }: any) => {
                    const { original } = row;
                    return <span>{getDisplayDateFormat(original.whenCreated, true)}</span>;
                },
            },
        ];

        return columns;
    }, []);

    return (
        <div className="user-administration-container">
            <div className="user-administration-content">
                <div className="f-c g-24 f-1">
                    <div className="f-r g-12 even">
                        <HeaderFilter
                            name="ad-users"
                            title="AD KORISNICI"
                            children={[]}
                        ></HeaderFilter>

                        <Input
                            name="ad-user-search"
                            id="ad-user-search"
                            type="text"
                            size="medium"
                            placeholder="Pretraži korisnike."
                            onChange={e => setSearch(e.target.value)}
                            value={search}
                            leftIcon={<SearchIcon className="search-icon" />}
                        />
                    </div>

                    <CustomTable
                        data={adUsers?.pagedModel.item ?? []}
                        columns={tableColumns}
                        setSortBy={setSortBy}
                        tableType={TableType.AdministrationTable}
                        loading={isLoading}
                        flex
                    />

                    {!isLoading && Boolean(adUsers) && adUsers?.pagedModel.count !== 0 && (
                        <Pagination
                            className="mt-24"
                            currentPage={currentPage}
                            numberOfPages={Math.ceil(
                                (adUsers?.pagedModel.count ?? 0) / parseInt(pageSize.value)
                            )}
                            pageNeighbours={PAGE_NEIGHBOURS}
                            pageSize={pageSize}
                            onPageSizeChange={v =>
                                handlePageSizeChange(v, setPageSize, setCurrentPage)
                            }
                            handlePageClick={e => handlePageClick(e, setCurrentPage)}
                            handlePageSelect={e => setCurrentPage(e)}
                            next={() => setCurrentPage(p => p + 1)}
                            prev={() => setCurrentPage(p => p - 1)}
                            first={() => setCurrentPage(1)}
                            last={() =>
                                setCurrentPage(
                                    Math.ceil(
                                        adUsers?.pagedModel.count ?? 0 / parseInt(pageSize.value)
                                    )
                                )
                            }
                        />
                    )}
                </div>
            </div>
        </div>
    );
};
